/**
 * PollingManager - Zentraler Service für optimiertes Polling
 *
 * PROBLEM GELÖST:
 * - Stoppt Polling bei inaktiven Tabs (Page Visibility API)
 * - Verhindert mehrfache Polling-Instanzen
 * - Exponential Backoff bei Fehlern
 * - Längere Intervalle bei Inaktivität
 */

class PollingManager {
  constructor() {
    this.intervals = new Map();
    this.isPageVisible = !document.hidden;
    this.lastUserActivity = Date.now();
    this.errorCounts = new Map();

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Track user activity
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
      document.addEventListener(event, this.handleUserActivity.bind(this), { passive: true });
    });
  }

  handleVisibilityChange() {
    this.isPageVisible = !document.hidden;

    if (!this.isPageVisible) {
      // Page is hidden - stop all polling
      console.log('[PollingManager] Page hidden - pausing all polling');
      this.pauseAll();
    } else {
      // Page is visible again - resume polling
      console.log('[PollingManager] Page visible - resuming polling');
      this.resumeAll();
    }
  }

  handleUserActivity() {
    this.lastUserActivity = Date.now();
    // Reset error counts on user activity
    this.errorCounts.clear();
  }

  /**
   * Register a polling function with smart management
   * @param {string} key - Unique identifier for this polling task
   * @param {Function} callback - Function to call
   * @param {Object} options - Polling options
   */
  register(key, callback, options = {}) {
    const {
      interval = 60000, // Default: 60 seconds (was 10-30s before!)
      immediate = true,
      inactiveMultiplier = 3, // Triple interval when inactive
      maxInterval = 300000, // Max 5 minutes
      errorBackoff = true
    } = options;

    // Clear existing interval if any
    this.unregister(key);

    const config = {
      callback,
      baseInterval: interval,
      currentInterval: interval,
      inactiveMultiplier,
      maxInterval,
      errorBackoff,
      isPaused: false
    };

    // Execute immediately if requested
    if (immediate && this.isPageVisible) {
      this.executeCallback(key, config);
    }

    // Start polling
    this.startPolling(key, config);

    // Store config
    this.intervals.set(key, config);

    console.log(`[PollingManager] Registered: ${key} (interval: ${interval}ms)`);
  }

  startPolling(key, config) {
    if (config.isPaused || !this.isPageVisible) {
      return;
    }

    const timerId = setInterval(() => {
      // Check if page is visible and user is active
      if (!this.isPageVisible) {
        return; // Skip if page is hidden
      }

      // Check user activity (increase interval if inactive)
      const timeSinceActivity = Date.now() - this.lastUserActivity;
      const isInactive = timeSinceActivity > 60000; // 1 minute of inactivity

      if (isInactive) {
        config.currentInterval = Math.min(
          config.baseInterval * config.inactiveMultiplier,
          config.maxInterval
        );
      } else {
        config.currentInterval = config.baseInterval;
      }

      this.executeCallback(key, config);
    }, config.currentInterval);

    config.timerId = timerId;
  }

  async executeCallback(key, config) {
    try {
      await config.callback();
      // Reset error count on success
      this.errorCounts.delete(key);
    } catch (error) {
      console.error(`[PollingManager] Error in ${key}:`, error);

      if (config.errorBackoff) {
        // Implement exponential backoff
        const errorCount = (this.errorCounts.get(key) || 0) + 1;
        this.errorCounts.set(key, errorCount);

        // Double interval for each error, max 5 minutes
        const backoffInterval = Math.min(
          config.baseInterval * Math.pow(2, errorCount),
          300000
        );

        if (backoffInterval !== config.currentInterval) {
          console.log(`[PollingManager] Backing off ${key} to ${backoffInterval}ms due to errors`);
          config.currentInterval = backoffInterval;

          // Restart with new interval
          clearInterval(config.timerId);
          this.startPolling(key, config);
        }
      }
    }
  }

  unregister(key) {
    const config = this.intervals.get(key);
    if (config && config.timerId) {
      clearInterval(config.timerId);
      this.intervals.delete(key);
      console.log(`[PollingManager] Unregistered: ${key}`);
    }
  }

  pauseAll() {
    this.intervals.forEach((config, key) => {
      if (config.timerId) {
        clearInterval(config.timerId);
        config.isPaused = true;
      }
    });
  }

  resumeAll() {
    this.intervals.forEach((config, key) => {
      if (config.isPaused) {
        config.isPaused = false;
        this.startPolling(key, config);
        this.executeCallback(key, config);
      }
    });
  }

  // Clean up all intervals
  destroy() {
    this.intervals.forEach((config, key) => {
      this.unregister(key);
    });

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
      document.removeEventListener(event, this.handleUserActivity);
    });
  }

  // Get current status
  getStatus() {
    const status = {
      isPageVisible: this.isPageVisible,
      lastUserActivity: this.lastUserActivity,
      activePollers: []
    };

    this.intervals.forEach((config, key) => {
      status.activePollers.push({
        key,
        interval: config.currentInterval,
        isPaused: config.isPaused,
        hasErrors: this.errorCounts.has(key)
      });
    });

    return status;
  }
}

// Export singleton instance
const pollingManager = new PollingManager();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.__pollingManager = pollingManager;
}

export default pollingManager;
